"use client";

import { useEffect, useRef, useMemo } from "react";
import createGlobe, { COBEOptions } from "cobe";
import { useMotionValue, useSpring } from "motion/react";

import { cn } from "@/lib/utils";

const MOVEMENT_DAMPING = 1400;

const DEFAULT_LOCATIONS = [
  { location: [-26.92, -49.0658] as [number, number], size: 0.07 }, // Blumenau, Brazil
  { location: [52.3676, 4.9041] as [number, number], size: 0.08 }, // Amsterdam, Netherlands
  { location: [13.7563, 100.5018] as [number, number], size: 0.08 }, // Bangkok, Thailand
  { location: [31.0461, 34.8516] as [number, number], size: 0.07 }, // Israel
  { location: [-25.0423, 117.7932] as [number, number], size: 0.07 }, // Western Australia
  { location: [-35.2809, 149.13] as [number, number], size: 0.06 }, // Canberra, Australia
  { location: [-33.4489, -70.6693] as [number, number], size: 0.08 }, // Santiago, Chile
  { location: [19.4326, -99.1332] as [number, number], size: 0.1 }, // Mexico City, Mexico
];

const DEFAULT_GLOBE_CONFIG: COBEOptions = {
  width: 800,
  height: 800,
  onRender: () => {},
  devicePixelRatio: 2,
  phi: 0,
  theta: 0.3,
  dark: 0,
  diffuse: 0.8,
  mapBrightness: 0.8,
  glowColor: [0.1, 0.4, 1],
  baseColor: [1, 1, 1],
  mapSamples: 32000,
  markerColor: [0 / 255, 115 / 255, 255 / 255],
  opacity: 0.1,
  markers: DEFAULT_LOCATIONS,
};

export interface LocationData {
  latitude: string;
  longitude: string;
  country: string;
  city: string;
}

export function Globe({ className, locationData = [], config }: { className?: string; locationData?: LocationData[]; config?: Partial<COBEOptions> }) {
  const phiRef = useRef(0);
  const widthRef = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointerInteracting = useRef<number | null>(null);
  const pointerInteractionMovement = useRef(0);

  const r = useMotionValue(0);
  const rs = useSpring(r, {
    mass: 1,
    damping: 30,
    stiffness: 100,
  });

  const updatePointerInteraction = (value: number | null) => {
    pointerInteracting.current = value;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = value !== null ? "grabbing" : "grab";
    }
  };

  const updateMovement = (clientX: number) => {
    if (pointerInteracting.current !== null) {
      const delta = clientX - pointerInteracting.current;
      pointerInteractionMovement.current = delta;
      r.set(r.get() + delta / MOVEMENT_DAMPING);
    }
  };

  // Convert location data to markers format and merge with default locations
  const markers = useMemo(
    () => [
      ...DEFAULT_LOCATIONS,
      ...locationData
        .filter((loc) => loc.latitude && loc.longitude)
        .map((loc) => ({
          location: [parseFloat(loc.latitude), parseFloat(loc.longitude)] as [number, number],
          size: 0.08,
        })),
    ],
    [locationData],
  );

  const globeConfig: COBEOptions = useMemo(
    () => ({
      ...DEFAULT_GLOBE_CONFIG,
      ...config,
      markers,
    }),
    [markers, config],
  );

  useEffect(() => {
    const onResize = () => {
      if (canvasRef.current) {
        widthRef.current = canvasRef.current.offsetWidth;
      }
    };

    window.addEventListener("resize", onResize);
    onResize();

    const globe = createGlobe(canvasRef.current!, {
      ...globeConfig,
      width: widthRef.current * 2,
      height: widthRef.current * 2,
      onRender: (state) => {
        if (!pointerInteracting.current) phiRef.current += 0.002;
        state.phi = phiRef.current + rs.get();
        state.width = widthRef.current * 2;
        state.height = widthRef.current * 2;
      },
    });

    setTimeout(() => (canvasRef.current!.style.opacity = "1"), 0);
    return () => {
      globe.destroy();
      window.removeEventListener("resize", onResize);
    };
  }, [rs, globeConfig]);

  return (
    <div className={cn("relative mx-auto aspect-[1/1] w-full max-w-[800px]", className)}>
      <canvas
        className="size-full opacity-0 transition-opacity duration-500 [contain:layout_paint_size]"
        style={{
          mixBlendMode: "screen",
          filter: "blur(0.5px) brightness(0.95)",
        }}
        ref={canvasRef}
        onPointerDown={(e) => {
          pointerInteracting.current = e.clientX;
          updatePointerInteraction(e.clientX);
        }}
        onPointerUp={() => updatePointerInteraction(null)}
        onPointerOut={() => updatePointerInteraction(null)}
        onMouseMove={(e) => updateMovement(e.clientX)}
        onTouchMove={(e) => e.touches[0] && updateMovement(e.touches[0].clientX)}
      />
    </div>
  );
}
